import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, IconButton, Input, InputGroup, InputNumber, Slider, toaster, Toggle} from "rsuite";
import {confirm} from "@rsuite/interactions";
import moment from "moment";
import {
  GetDevice_GetDeviceResponse,
  LedPanelService,
  ReadLedPanelParams_ReadLedPanelParamsResponse
} from "../../../../../api";
import NotificationFactory from "../../../../../shared/Notifications";
import {TfiSaveIcon, VscEditIcon} from "../../../../../models/Icons.model";
import {ExternalDeviceType} from "../../ExternalDevices.model";
import {ExternalDevicesUtils} from "../../ExternalDevices.utils";
import {formatTimezoneOffset} from "../../../../../shared/Time.utils";
import {SharedApi} from "../../../../../services/Shared.api";
import DateTimeEditModal from "../../../../modals/DateTimeEditModal/DateTimeEditModal";
import DevicesModal from "../../../../modals/DeviceModal/DeviceModal";
import {DeviceListItem, DevicesResponse} from "../../../../modals/modals.model";
import DeviceEvents from "../../../../shared/DeviceEvents/DeviceEvents";
import DeviceParameterSyncButton from "../../../../shared/DeviceParameterSyncButton/DeviceParameterSyncButton";
import RebootConfirmationModal from "../Dimmer/components/RebootConfirmationModal";
import {extractTimezoneFromISO} from "../Dimmer/utils/Dimmer.utils";
import {
  ColorControlProps,
  LedPanelClockStyle,
  LedPanelColorKind,
  LedPanelControlValue,
  LedPanelDotMode,
  LedPanelDurations,
  LedPanelMeteo,
  LedPanelOperatingMode,
  LedPanelProps,
  RgbColor
} from "./LedPanel.model";
import "./LedPanel.scss";
import {
  clockStyles,
  defaultMeteoSourceLabel,
  dotModes,
  emptyColor,
  emptyDurations,
  emptyMeteo,
  formatMeteoSourceLabel,
  formatUnknownMeteoSourceLabel,
  fromHex,
  getDurationFields,
  getMeteoSourceId,
  getMeteoSourceLabel,
  meteoFields,
  normalizeDuration,
  normalizeMeteoValue,
  operatingModes,
  toColor,
  toDurations,
  toMeteo,
  toScreenDelay,
  toHex
} from "./LedPanel.utils";

const ColorControl = ({label, value, disabled, onChange, onSave}: ColorControlProps) => (
  <InputGroup className="led-panel__color-control mb-2">
    <InputGroup.Addon className="led-panel__panel-label">{label}</InputGroup.Addon>
    <input
      aria-label={label}
      className="led-panel__color"
      type="color"
      value={toHex(value)}
      disabled={disabled}
      onChange={(event) => onChange(fromHex(event.target.value))}
    />
    <span className="led-panel__color-value">{value.red}, {value.green}, {value.blue}</span>
    <InputGroup.Addon>
      <TfiSaveIcon
        title={`Сохранить: ${label}`}
        style={{cursor: disabled ? "default" : "pointer"}}
        onClick={() => !disabled && onSave()}
      />
    </InputGroup.Addon>
  </InputGroup>
);

const LedPanel = ({id, deviceId, isAdmin}: LedPanelProps) => {
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState<ReadLedPanelParams_ReadLedPanelParamsResponse>({});
  const [brightness, setBrightness] = useState(0);
  const [clockColor, setClockColor] = useState<RgbColor>(emptyColor);
  const [headerColor, setHeaderColor] = useState<RgbColor>(emptyColor);
  const [footerColor, setFooterColor] = useState<RgbColor>(emptyColor);
  const [durations, setDurations] = useState<LedPanelDurations>(emptyDurations);
  const [meteo, setMeteo] = useState<LedPanelMeteo>(emptyMeteo);
  const [workdays, setWorkdays] = useState<number | null>(null);
  const [serialEnabled, setSerialEnabled] = useState(false);
  const [isMeteoModalOpen, setIsMeteoModalOpen] = useState(false);
  const [meteoSourceId, setMeteoSourceId] = useState<string | null>(null);
  const [meteoSourceLabel, setMeteoSourceLabel] = useState(defaultMeteoSourceLabel);
  const [deviceInfo, setDeviceInfo] = useState<GetDevice_GetDeviceResponse | null>(null);
  const [deviceTime, setDeviceTime] = useState<Date | null>(null);
  const [userTimezone, setUserTimezone] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [currentUtcDateTime, setCurrentUtcDateTime] = useState("");
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [showRebootModal, setShowRebootModal] = useState(false);
  const timeMomentRef = useRef<moment.Moment | null>(null);
  const timeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const resolveMeteoSourceLabel = useCallback(async (selectedId: string | null) => {
    if (!selectedId) {
      setMeteoSourceLabel(defaultMeteoSourceLabel);
      return;
    }
    if (!deviceId) {
      setMeteoSourceLabel(formatUnknownMeteoSourceLabel(selectedId));
      return;
    }

    try {
      const response = (await SharedApi.getDevices(deviceId)) as DevicesResponse;
      setMeteoSourceLabel(getMeteoSourceLabel(response, selectedId));
    } catch (error) {
      console.error("Ошибка получения источника метеоданных LED-экрана:", error);
      toaster.push(
        NotificationFactory.error("Не удалось получить источник метеоданных"),
        {placement: "topEnd"}
      );
      setMeteoSourceLabel(formatUnknownMeteoSourceLabel(selectedId));
    }
  }, [deviceId]);

  const load = useCallback(async () => {
    setLoading(true);
    const showLoadError = (dataName: string, error: unknown) => {
      console.error(`Ошибка получения данных «${dataName}»:`, error);
      toaster.push(
        NotificationFactory.error(`Не удалось получить ${dataName}`),
        {placement: "topEnd"}
      );
    };

    const loadPanelParams = async () => {
      try {
        const response = await LedPanelService.getApiLedPanelParams(id);
        const clockParams = response.electronicClockParams ?? {};
        const responseDurations = clockParams.displayDurations ?? {};
        const storedMeteo = clockParams.storedMeteo ?? {};
        const selectedSourceId = clockParams.meteoDevice?.deviceId ?? null;

        setParams(response);
        setBrightness(Number(clockParams.displayBrightness ?? 0));
        setClockColor(toColor(response.clockColor));
        setHeaderColor(toColor(response.headerColor));
        setFooterColor(toColor(response.footerColor));
        setDurations(toDurations(responseDurations));
        setMeteo(toMeteo(storedMeteo));
        setMeteoSourceId(selectedSourceId);
        await resolveMeteoSourceLabel(selectedSourceId);
      } catch (error) {
        showLoadError("параметры LED-экрана", error);
      }
    };

    const loadWorkdays = async () => {
      try {
        const response = await LedPanelService.getApiLedPanelWorkdays(id);
        setWorkdays(response.workdays ?? null);
      } catch (error) {
        showLoadError("количество рабочих дней", error);
      }
    };

    const loadTime = async () => {
      try {
        const response = await SharedApi.getTime(id);
        if (!response.time) {
          setUserTimezone("");
          setDeviceTime(null);
          return;
        }

        setUserTimezone(extractTimezoneFromISO(response.time));
        const match = response.time.match(
          /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/
        );
        const parsedTime = match
          ? new Date(
            parseInt(match[1], 10),
            parseInt(match[2], 10) - 1,
            parseInt(match[3], 10),
            parseInt(match[4], 10),
            parseInt(match[5], 10),
            parseInt(match[6], 10)
          )
          : new Date(response.time);

        if (Number.isNaN(parsedTime.getTime())) {
          throw new Error(`Некорректное время устройства: ${response.time}`);
        }
        setDeviceTime(parsedTime);
      } catch (error) {
        setUserTimezone("");
        setDeviceTime(null);
        showLoadError("время устройства", error);
      }
    };

    const loadDeviceInfo = async () => {
      try {
        const response = await SharedApi.getDeviceInfo(id);
        setDeviceInfo(response);
      } catch (error) {
        showLoadError("информацию об устройстве", error);
      }
    };

    await Promise.allSettled([
      loadPanelParams(),
      loadWorkdays(),
      loadTime(),
      loadDeviceInfo()
    ]);
    setLoading(false);
  }, [id, resolveMeteoSourceLabel]);

  useEffect(() => {
    load().then();
  }, [load]);

  useEffect(() => {
    const updateUtcTime = () => {
      setCurrentUtcDateTime(moment().utc().format("DD.MM.YYYY HH:mm:ss"));
    };

    updateUtcTime();
    const utcInterval = setInterval(updateUtcTime, 1000);
    return () => clearInterval(utcInterval);
  }, []);

  useEffect(() => {
    if (timeIntervalRef.current) {
      clearInterval(timeIntervalRef.current);
    }

    if (!deviceTime) {
      timeMomentRef.current = null;
      setCurrentTime("");
      return;
    }

    timeMomentRef.current = moment(deviceTime);
    setCurrentTime(timeMomentRef.current.format("DD.MM.YYYY HH:mm:ss"));

    timeIntervalRef.current = setInterval(() => {
      if (timeMomentRef.current) {
        timeMomentRef.current.add(1, "second");
        setCurrentTime(timeMomentRef.current.format("DD.MM.YYYY HH:mm:ss"));
      }
    }, 1000);

    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
      }
    };
  }, [deviceTime]);

  const update = async (action: () => PromiseLike<unknown>, successMessage: string, refresh = true) => {
    setLoading(true);
    try {
      await action();
      toaster.push(NotificationFactory.success(successMessage), {placement: "topEnd"});
      if (refresh) await load();
      return true;
    } catch (error) {
      console.error(`Ошибка обновления LED-экрана: ${successMessage}`, error);
      toaster.push(NotificationFactory.error("Не удалось сохранить настройку"), {placement: "topEnd"});
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleTimezoneChange = (value: string) => {
    if (/^[+-]?\d*$/.test(value)) {
      setUserTimezone(value);
    }
  };

  const saveTimezone = () => update(
    () => SharedApi.updateTimezone(id, formatTimezoneOffset(userTimezone)),
    "Часовой пояс обновлён"
  );

  const saveDeviceTime = async (newDate: Date) => {
    const saved = await update(
      () => SharedApi.updateUTCTime(id, newDate),
      "Время и дата обновлены"
    );
    if (saved) {
      setIsTimeModalOpen(false);
    }
  };

  const setDeviceInfoField = (field: "article" | "model" | "place", value: string) => {
    setDeviceInfo((current) => current ? {...current, [field]: value} : current);
  };

  const saveDeviceInfoField = (
    field: "article" | "model" | "place",
    successMessage: string
  ) => {
    const actions = {
      article: () => SharedApi.updateArticle(id, deviceInfo?.article ?? ""),
      model: () => SharedApi.updateModel(id, deviceInfo?.model ?? ""),
      place: () => SharedApi.updatePlace(id, deviceInfo?.place ?? "")
    };
    return update(actions[field], successMessage);
  };

  const handleConfirmReboot = async () => {
    setShowRebootModal(false);
    setLoading(true);
    try {
      await SharedApi.rebootDevice(id);
      toaster.push(NotificationFactory.success("Команда перезапуска отправлена"), {
        placement: "topEnd"
      });
    } catch (error) {
      console.error("Ошибка перезапуска LED-экрана:", error);
      toaster.push(NotificationFactory.error("Не удалось перезапустить устройство"), {
        placement: "topEnd"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOperatingMode = async (value: LedPanelControlValue) => {
    if (!value || value === params.operatingMode) return;
    const approved = await confirm(
      "Смена режима может привести к перезагрузке LED-экрана. Продолжить?",
      {okButtonText: "Продолжить", cancelButtonText: "Отмена"}
    );
    if (!approved) return;

    await update(
      () => LedPanelService.postApiLedPanelOperatingMode(id, {mode: value as LedPanelOperatingMode}),
      "Режим работы обновлён"
    );
  };

  const saveColor = (kind: LedPanelColorKind, value: RgbColor) => {
    const methods = {
      clock: LedPanelService.postApiLedPanelClockColor,
      header: LedPanelService.postApiLedPanelHeaderColor,
      footer: LedPanelService.postApiLedPanelFooterColor
    };
    return update(() => methods[kind](id, value), "Цвет обновлён");
  };

  const setDuration = (field: keyof LedPanelDurations, value: LedPanelControlValue) => {
    setDurations((current) => ({...current, [field]: normalizeDuration(value)}));
  };

  const toggleDuration = (field: keyof LedPanelDurations, enabled: boolean) => {
    setDurations((current) => ({
      ...current,
      [field]: enabled ? Math.max(1, current[field]) : 0
    }));
  };

  const saveDurations = () => update(
    () => LedPanelService.postApiLedPanelScreenDelay(id, toScreenDelay(durations)),
    "Длительности экранов обновлены"
  );

  const setMeteoValue = (field: keyof LedPanelMeteo, value: LedPanelControlValue) => {
    setMeteo((current) => ({...current, [field]: normalizeMeteoValue(value)}));
  };

  const saveMeteo = () =>
    update(() => LedPanelService.postApiLedPanelMeteo(id, meteo), "Метеоданные переданы");

  const selectMeteoSource = async (device: DeviceListItem) => {
    const selectedId = getMeteoSourceId(device);
    const saved = await update(
      () => LedPanelService.postApiLedPanelMeteoSource(id, {deviceId: selectedId}),
      "Источник метеоданных обновлён"
    );
    if (saved) {
      setMeteoSourceId(selectedId);
      setMeteoSourceLabel(formatMeteoSourceLabel(device));
    }
  };

  const electronicParams = params.electronicClockParams ?? {};

  return (
    <>
      <div className="fieldset-wrapper led-panel">
        <div className="legend-title main-legend-title">LED-экран iAura.Y</div>
        <section className="secondary-clock">
          <div className="row p-2 pt-1">
          <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mt-3">
          <section className="fieldset-wrapper control h-100 bg-white mt-0 pt-4 pb-3 led-panel__section">
            <div className="legend-title">Панель</div>
            <InputGroup className="led-panel__select-group mb-2" style={{padding: 0}}>
              <InputGroup.Addon className="led-panel__panel-label">Режим работы</InputGroup.Addon>
              <div className="led-panel__select-wrapper">
                <select
                  className="form-select"
                  style={{borderRadius: 0}}
                  value={params.operatingMode ?? ""}
                  disabled={loading}
                  onChange={(event) => handleOperatingMode(event.target.value)}
                >
                  <option value="">Не выбран</option>
                  {operatingModes.map(({label, value}) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </InputGroup>
            <InputGroup className="led-panel__select-group mb-2" style={{padding: 0}}>
              <InputGroup.Addon className="led-panel__panel-label">Стиль часов</InputGroup.Addon>
              <div className="led-panel__select-wrapper">
                <select
                  className="form-select"
                  style={{borderRadius: 0}}
                  value={params.clockStyle ?? ""}
                  disabled={loading}
                  onChange={(event) => event.target.value && update(
                    () => LedPanelService.postApiLedPanelClockStyle(
                      id,
                      {style: event.target.value as LedPanelClockStyle}
                    ),
                    "Стиль часов обновлён"
                  )}
                >
                  <option value="">Не выбран</option>
                  {clockStyles.map(({label, value}) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </InputGroup>
            <InputGroup className="led-panel__select-group mb-2" style={{padding: 0}}>
              <InputGroup.Addon className="led-panel__panel-label">Режим точек</InputGroup.Addon>
              <div className="led-panel__select-wrapper">
                <select
                  className="form-select"
                  style={{borderRadius: 0}}
                  value={params.dotMode ?? ""}
                  disabled={loading}
                  onChange={(event) => event.target.value && update(
                    () => LedPanelService.postApiLedPanelDotMode(
                      id,
                      {mode: event.target.value as LedPanelDotMode}
                    ),
                    "Режим точек обновлён"
                  )}
                >
                  <option value="">Не выбран</option>
                  {dotModes.map(({label, value}) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </InputGroup>
            <ColorControl label="Цвет часов" value={clockColor} disabled={loading} onChange={setClockColor} onSave={() => saveColor("clock", clockColor)}/>
            <ColorControl label="Цвет верхней области" value={headerColor} disabled={loading} onChange={setHeaderColor} onSave={() => saveColor("header", headerColor)}/>
            <ColorControl label="Цвет нижней области" value={footerColor} disabled={loading} onChange={setFooterColor} onSave={() => saveColor("footer", footerColor)}/>
            <div className="text-center mt-auto">
              <Button
                color="red"
                appearance="primary"
                type="button"
                loading={loading}
                disabled={loading}
                onClick={() => setShowRebootModal(true)}
              >
                Перезапустить устройство
              </Button>
            </div>
          </section>
          </div>

          <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mt-3">
          <section className="fieldset-wrapper control h-100 bg-white mt-0 pt-4 pb-3 led-panel__section">
            <div className="legend-title">Дисплей</div>
            <div className="led-panel__field">
              <span className="led-panel__field-label">Вкл / Выкл</span>
              <Toggle
                checked={Boolean(electronicParams.displayEnabled)}
                checkedChildren="ON"
                unCheckedChildren="OFF"
                loading={loading}
                onChange={(enabled) => update(
                  () => LedPanelService.postApiLedPanelDisplayState(id, {enabled}),
                  "Состояние дисплея обновлено"
                )}
              />
            </div>
            <div className="led-panel__field">
              <span className="led-panel__field-label">Вывести адрес устройства</span>
              <Toggle
                checked={serialEnabled}
                checkedChildren="ON"
                unCheckedChildren="OFF"
                loading={loading}
                onChange={async (enabled) => {
                  const saved = await update(
                    () => LedPanelService.postApiLedPanelSerialState(id, {enabled}),
                    "Serial-вывод обновлён",
                    false
                  );
                  if (saved) setSerialEnabled(enabled);
                }}
              />
            </div>
            <div className="mb-2">Яркость: {brightness}</div>
            <div className="led-panel__brightness-control">
              <Slider
                className="led-panel__brightness-slider"
                min={0}
                max={255}
                step={1}
                value={brightness}
                disabled={loading}
                onChange={setBrightness}
              />
              <IconButton
                size="sm"
                appearance="default"
                icon={<TfiSaveIcon/>}
                disabled={loading}
                title="Сохранить яркость"
                onClick={() => update(
                  () => LedPanelService.postApiLedPanelDisplayBrightness(id, {value: brightness}),
                  "Яркость обновлена"
                )}
              />
            </div>
            <InputGroup className="mt-3 led-panel__stacked-group">
              <InputGroup.Addon>Количество рабочих дней</InputGroup.Addon>
              <Input value={workdays || 0} readOnly/>
            </InputGroup>
            <div className="mt-2">
              <InputGroup className="mb-2 led-panel__stacked-group">
                <InputGroup.Addon className="led-panel__time-label">Время и дата</InputGroup.Addon>
                <Input className="bg-green" readOnly value={currentTime}/>
                <InputGroup.Addon
                  title="Редактировать время и дату"
                  style={{cursor: loading ? "default" : "pointer"}}
                  onClick={() => !loading && setIsTimeModalOpen(true)}
                >
                  <VscEditIcon/>
                </InputGroup.Addon>
              </InputGroup>
              <InputGroup className="mb-2 led-panel__stacked-group">
                <InputGroup.Addon className="led-panel__time-label">Час. пояс</InputGroup.Addon>
                <Input
                  value={userTimezone}
                  disabled={loading}
                  onChange={handleTimezoneChange}
                  style={{textAlign: "center"}}
                />
                <InputGroup.Addon>
                  <TfiSaveIcon
                    title="Сохранить часовой пояс"
                    style={{cursor: loading || !userTimezone ? "default" : "pointer"}}
                    onClick={() => !loading && userTimezone && saveTimezone()}
                  />
                </InputGroup.Addon>
              </InputGroup>
              <InputGroup className="led-panel__stacked-group">
                <InputGroup.Addon className="led-panel__time-label">Время и дата UTC</InputGroup.Addon>
                <Input className="bg-yellow" readOnly value={currentUtcDateTime}/>
              </InputGroup>
            </div>
          </section>
          </div>

          <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mt-3">
          <section className="fieldset-wrapper control h-100 bg-white mt-0 pt-4 led-panel__section">
            <div className="legend-title">Длительность экранов, сек.</div>
            {getDurationFields(electronicParams.sensorTemperature).map(([field, label]) => (
              <div className="d-flex align-items-center mb-2" key={field}>
                <Toggle
                  className="me-2"
                  size="md"
                  checked={durations[field] > 0}
                  loading={loading}
                  color="blue"
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                  onChange={(enabled) => toggleDuration(field, enabled)}
                />
                <InputGroup className="led-panel__number-group flex-grow-1">
                  <InputGroup.Addon>{label}</InputGroup.Addon>
                  <InputNumber
                    min={0}
                    max={99}
                    value={durations[field]}
                    disabled={loading}
                    onChange={(value) => setDuration(field, value)}
                  />
                  <InputGroup.Addon>
                    <TfiSaveIcon
                      title={`Сохранить: ${label}`}
                      style={{cursor: loading ? "default" : "pointer"}}
                      onClick={() => !loading && saveDurations()}
                    />
                  </InputGroup.Addon>
                </InputGroup>
              </div>
            ))}
          </section>
          </div>

          <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mt-3">
          <section className="fieldset-wrapper control h-100 bg-white mt-0 pt-4 pb-3 led-panel__section">
            <div className="legend-title">Метеоданные</div>
            <div className="led-panel__field">
              <span className="led-panel__field-label">Источник</span>
              <Button
                className="led-panel__source-button"
                size="xs"
                appearance="subtle"
                disabled={loading || !deviceId}
                title={meteoSourceId ?? "По координатам"}
                onClick={() => setIsMeteoModalOpen(true)}
              >
                {meteoSourceLabel}
              </Button>
            </div>
            {meteoFields.map(([field, label]) => (
              <InputGroup className="mb-2 led-panel__number-group" key={field}>
                <InputGroup.Addon>{label}</InputGroup.Addon>
                <InputNumber
                  min={field === "temperature" ? undefined : 0}
                  value={meteo[field]}
                  disabled={loading}
                  onChange={(value) => setMeteoValue(field, value)}
                />
                <InputGroup.Addon>
                  <TfiSaveIcon
                    title={`Передать на экран: ${label}`}
                    style={{cursor: loading ? "default" : "pointer"}}
                    onClick={() => !loading && saveMeteo()}
                  />
                </InputGroup.Addon>
              </InputGroup>
            ))}
          </section>
          </div>

          <div className="col-12 mt-3">
          <section className="fieldset-wrapper control h-100 bg-white mt-0 pt-4 pb-3 led-panel__section">
            <div className="legend-title">Информация об устройстве</div>
            <div className="row">
              <div className="col-lg-6 col-md-12">
                <InputGroup className="mb-2 led-panel__stacked-group">
                  <InputGroup.Addon className="led-panel__info-label">Артикул</InputGroup.Addon>
                  <Input
                    value={deviceInfo?.article ?? ""}
                    disabled={loading}
                    onChange={(value) => setDeviceInfoField("article", value)}
                  />
                  {isAdmin && (
                    <InputGroup.Addon>
                      <TfiSaveIcon
                        title="Сохранить артикул"
                        style={{cursor: loading ? "default" : "pointer"}}
                        onClick={() => !loading && saveDeviceInfoField("article", "Артикул обновлён")}
                      />
                    </InputGroup.Addon>
                  )}
                </InputGroup>
                <InputGroup className="mb-2 led-panel__stacked-group">
                  <InputGroup.Addon className="led-panel__info-label">Дата производства</InputGroup.Addon>
                  <Input value={deviceInfo?.fabricDate ?? ""} readOnly/>
                </InputGroup>
                <InputGroup className="mb-2 led-panel__stacked-group">
                  <InputGroup.Addon className="led-panel__info-label">Версия прошивки</InputGroup.Addon>
                  <Input value={deviceInfo?.firmwareVersion ?? ""} readOnly/>
                  {isAdmin && (
                    <DeviceParameterSyncButton
                      deviceId={id}
                      parameter="FirmwareVersion"
                      onSynced={load}
                    />
                  )}
                </InputGroup>
              </div>
              <div className="col-lg-6 col-md-12">
                <InputGroup className="mb-2 led-panel__stacked-group">
                  <InputGroup.Addon className="led-panel__info-label">Модель</InputGroup.Addon>
                  <Input
                    value={deviceInfo?.model ?? ""}
                    disabled={loading}
                    onChange={(value) => setDeviceInfoField("model", value)}
                  />
                  {isAdmin && (
                    <InputGroup.Addon>
                      <TfiSaveIcon
                        title="Сохранить модель"
                        style={{cursor: loading ? "default" : "pointer"}}
                        onClick={() => !loading && saveDeviceInfoField("model", "Модель обновлена")}
                      />
                    </InputGroup.Addon>
                  )}
                </InputGroup>
                <InputGroup className="mb-2 led-panel__stacked-group">
                  <InputGroup.Addon className="led-panel__info-label">Серийный номер</InputGroup.Addon>
                  <Input value={ExternalDevicesUtils.formatDeviceSerialNumber(deviceInfo?.serialNumber, ExternalDeviceType.LedPanel)} readOnly/>
                </InputGroup>
                <InputGroup className="mb-2 led-panel__stacked-group">
                  <InputGroup.Addon className="led-panel__info-label">Место установки</InputGroup.Addon>
                  <Input
                    value={deviceInfo?.place ?? ""}
                    disabled={loading}
                    onChange={(value) => setDeviceInfoField("place", value)}
                  />
                  {isAdmin && (
                    <DeviceParameterSyncButton
                      deviceId={id}
                      parameter="Place"
                      onSynced={load}
                    />
                  )}
                  <InputGroup.Addon>
                    <TfiSaveIcon
                      title="Сохранить место установки"
                      style={{cursor: loading ? "default" : "pointer"}}
                      onClick={() => !loading && saveDeviceInfoField("place", "Место установки обновлено")}
                    />
                  </InputGroup.Addon>
                </InputGroup>
              </div>
            </div>
          </section>
          </div>
          </div>
        </section>
      </div>

      <div className="row px-2 pb-2">
        <div className="col-md-12">
          <DeviceEvents deviceId={id}/>
        </div>
      </div>
      <DevicesModal
        open={isMeteoModalOpen}
        deviceId={deviceId}
        onClose={() => setIsMeteoModalOpen(false)}
        onSelectDevice={selectMeteoSource}
      />
      <DateTimeEditModal
        open={isTimeModalOpen}
        onClose={() => setIsTimeModalOpen(false)}
        initialValue={deviceTime}
        loading={loading}
        title="Редактировать дату и время"
        onSave={async (newMoment) => {
          await saveDeviceTime(newMoment.toDate());
        }}
      />
      <RebootConfirmationModal
        show={showRebootModal}
        onHide={() => setShowRebootModal(false)}
        onConfirm={handleConfirmReboot}
      />
    </>
  );
};

export default LedPanel;
